import AssessmentSkillSetCharts from '@components/Charts/AssessmentSkillSetCharts'
import {
  AssessmentColumnType as ColumnType,
  useAssessmentTable
} from '@suntex.fim/assessment'
import { Button } from '@components/ui'
import { useAssessmentDropdownFilters } from '@hooks/useAssessmentDropdownFilters'
import { useQuery } from '@tanstack/react-query'
import {
  GetAPIparams,
  renderTitle,
  useDropdownMenu
} from '@utils/assessmentReportUtils'
import { Flex } from 'antd'
import { useMemo, useState } from 'react'

import { useLocation } from '@suntex.core/nav'
import { TeamId } from '@suntex.fim/api-client'
import {
  getAssessmentReportData,
  schoolAssessmentInitialSorting,
  teamAssessmentInitialSorting
} from '@suntex.fim/assessment'
import { isAuthenticated, useAuth } from '@suntex.fim/auth/react'
import { $EntityLevel, EntityLevel } from '@suntex.fim/enums'
import { useFramework } from '@suntex.fim/framework'
import { SendMessage } from '@suntex.fim/messages'
import { useOptionalTeamInfo } from '@suntex.fim/team'
import { ExportMenu, LoaderOverlay, SearchInput } from '@suntex.fim/ui'
import { getSkillSetsMeta } from '@suntex.fim/utils'

import AssessmentReportNavigation from './AssessmentReport/AssessmentReportNavigation'
import columns, {
  SkillSetsReportData,
  hidableSelectLabel,
  schoolHidableSelectLabel
} from './AssessmentReport/AssessmentReportSkillSetGamesTable'
import AssessmentReportSummaryDashboard from './AssessmentReport/AssessmentReportSummaryDashboard'
import FactFluency from './AssessmentReport/FactFluency'
import SelectedRowPopUp from './AssessmentReport/SelectedRowPopUp'

/**
 * returns JSX for skill set games report
 */
function AssessmentReportSkillSetGames() {
  const { api } = useFramework()
  const location = useLocation()
  const { login } = useAuth(isAuthenticated)
  const isSchool =
    login.entityLevel === EntityLevel.School &&
    location.pathname.includes('school')
  const team = useOptionalTeamInfo()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [dropdownFilters, setDropDownFilters] = useAssessmentDropdownFilters()

  const { id, year, grade } = GetAPIparams(isSchool, dropdownFilters)
  const [searchQuery, setSearchQuery] = useState('')
  const [sendMessageData, setSendMessageData] = useState<any[]>([])
  const [isExporting, setIsExporting] = useState(false)

  const { data: assessmentReportData, isLoading } = useQuery({
    queryKey: ['assessment', 'skillSetsGames', id, isSchool, year, grade],
    queryFn: async () => {
      if (isSchool && login.entityLevel) {
        const response =
          await api.get_Assessment_SkillSetsPercentAggregateByLevel({
            id: id!,
            level: login.entityLevel,
            year: year!,
            aggregateLevel: $EntityLevel.from(login.entityLevel - 1),
            grade
          })
        return response.data || []
      } else {
        const response = await api.get_Assessment_SkillSetsTeam({
          teamId: id as TeamId
        })
        return response.teamSkillSets || []
      }
    },
    enabled: id != null
  })

  const assessmentReport = getAssessmentReportData(assessmentReportData)

  const { data: teamSummary = {}, isLoading: isSummaryLoading } = useQuery({
    queryKey: ['assessment', 'skillSetsSummary', id, year, grade],
    queryFn: async () => {
      const response =
        await api.get_Assessment_SkillSetsPercentAggregateByLevel({
          id: id!,
          level: EntityLevel.Team,
          year: year!,
          aggregateLevel: EntityLevel.Team,
          grade
        })
      return response.data?.[0]?.ss ?? {}
    },
    enabled: !isSchool && id != null && year != null
  })
  /**
   * open modal for send message and set sendMessageData state value
   */
  const sendMessage = (data: any[]) => {
    setIsModalOpen(true)
    setSendMessageData(data)
  }
  const memoColumns = useMemo<ColumnType<SkillSetsReportData>[]>(
    () =>
      isSchool
        ? columns({ graphColor: '#E16D6D' })
        : columns({ graphColor: '#E16D6D', sendMessage }),
    []
  )
  const linkOptions = getSkillSetsMeta().map((ss) => ({
    key: `word-problems/${ss.label}`,
    label: ss.label
  }))
  /**
   * Initialize usetable with initial data and
   * Destructure table and Table from useTable hooks
   */
  const { table, Table } = useAssessmentTable<SkillSetsReportData>({
    data: assessmentReport,
    columns: memoColumns,
    isLoading,
    globalFilter: searchQuery,
    horizontalScroll: true,
    initialSorting: isSchool
      ? schoolAssessmentInitialSorting
      : teamAssessmentInitialSorting
  })

  const selectedRow = table.getSelectedRowModel().flatRows

  const isDisabled = !selectedRow.length

  const sendMessageUsers = sendMessageData.length
    ? sendMessageData
    : selectedRow
  const selectedUserOptions = sendMessageUsers.map(
    ({ original }: { original: any }) => ({
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      label: original.displayName || original.userId,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      value: original.playerId
    })
  )
  const fileName = isSchool
    ? `${login.schoolName}_skill-set-games`
    : `${team?.name}_skill-set-games`
  const exportColumn = isSchool ? schoolHidableSelectLabel : hidableSelectLabel
  const chartData = isSchool ? (assessmentReport[0]?.ss ?? {}) : teamSummary

  const items = useDropdownMenu({
    table,
    hidableSelectLabel: exportColumn,
    fileName,
    hasColumnChange: isSchool ? ['grade'] : [],
    setIsLoading: setIsExporting
  })

  return (
    <>
      {/* navigation */}
      <Flex vertical gap="20px" style={{ position: 'relative' }}>
        {/* Loader overlay when loading and popup not open */}
        <LoaderOverlay
          isLoading={isLoading || isSummaryLoading || isExporting}
        />
        <AssessmentReportNavigation
          exportButton={
            <ExportMenu
              ariaLabel="Export report options"
              items={items}
              trigger={['click']}
            />
          }
          dropdownFilters={dropdownFilters}
          setDropDownFilters={setDropDownFilters}
          isSchool={isSchool}
        />
        <Flex className="tiles-container" wrap="wrap">
          <Flex className="left" vertical gap="20px" flex="1 1 560px">
            <FactFluency type="red" />
            <AssessmentReportSummaryDashboard
              dropdownFilters={dropdownFilters}
              isSchool={isSchool}
            />
          </Flex>
          <Flex className="right right-graph-box" flex="1 1 560px">
            <Flex vertical className="graph-tiles" justify="space-between">
              {renderTitle(isSchool)}
              <Flex vertical align="center" className="graph-tiles__graph">
                <div className="graph-container">
                  <AssessmentSkillSetCharts
                    backgroundColor="#E16D6D"
                    chartData={chartData}
                  />
                </div>
              </Flex>
            </Flex>
          </Flex>
        </Flex>
        {/* result */}
        <div className="main-result">
          {/* Updated toolbar design */}
          <Flex
            justify="space-between"
            align="center"
            className="main-result__toolbar"
            wrap
          >
            <h4 className="title">
              {isSchool ? 'School Players' : 'Team Players'}
            </h4>

            <Flex gap="10px" justify="space-between" className="print-hidden">
              <div className="report-search">
                <SearchInput value={searchQuery} onChange={setSearchQuery} />
              </div>
              {!isSchool && (
                <Button
                  intent="tertiary"
                  size="small"
                  disabled={isDisabled}
                  onClick={() => setIsModalOpen(true)}
                >
                  SEND MESSAGE
                </Button>
              )}
            </Flex>
          </Flex>
          <Table />
        </div>
        {!isModalOpen && (
          <SelectedRowPopUp
            selectedRow={selectedRow}
            resetRowSelection={table.resetRowSelection}
            handleSendMessage={() => setIsModalOpen(true)}
          />
        )}
        <SendMessage
          linkOptions={linkOptions}
          isModalOpen={isModalOpen}
          closeModal={(isSuccess) => {
            setIsModalOpen(false)
            setSendMessageData([])
            if (isSuccess) {
              table.resetRowSelection()
            }
          }}
          selectedUserOptions={selectedUserOptions}
          recipientEntityId={team?.id}
          reportType="Skill Set Games"
        />
      </Flex>
    </>
  )
}

export default AssessmentReportSkillSetGames
